// frontend/src/hooks/useExtensionSubscription.js
// Hook for extension-specific subscription status

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from '../api/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * Extension tier types - separate from webapp tiers
 * @typedef {'free' | 'trial' | 'trial_expired' | 'pro'} ExtensionTier
 */

/**
 * Extension subscription status
 * @typedef {Object} ExtensionSubscriptionStatus
 * @property {ExtensionTier} tier - Current tier
 * @property {boolean} trialActive - Is trial active
 * @property {number} trialDaysRemaining - Days left in trial
 * @property {string|null} trialStartDate - ISO date when trial started
 * @property {boolean} canSync - Can user sync sessions
 * @property {number} cloudSessionCount - Current synced sessions
 * @property {number|null} cloudSessionLimit - Max sessions (null = unlimited)
 * @property {Object} pricing - Pricing info
 * @property {number} pricing.price - Current price
 * @property {boolean} pricing.isDiscounted - Is early adopter discount active
 * @property {number} pricing.daysRemaining - Days left for discount
 * @property {number} pricing.canonicalPrice - Regular price ($2.99)
 * @property {number} pricing.discountedPrice - Discounted price ($0.99)
 * @property {Object} features - Available features
 * @property {boolean} features.unlimitedSync - Can sync unlimited sessions
 * @property {boolean} features.bulkExport - Can bulk export
 * @property {boolean} features.voiceRecording - Can record voice
 * @property {boolean} features.voiceView - Can view voice recordings
 * @property {boolean} features.multiDevice - Can use multiple devices
 * @property {number} deviceLimit - Max devices allowed
 */

const ExtensionSubscriptionContext = createContext(null);

export const useExtensionSubscription = () => {
  const context = useContext(ExtensionSubscriptionContext);
  if (!context) {
    throw new Error('useExtensionSubscription must be used within ExtensionSubscriptionProvider');
  }
  return context;
};

export const ExtensionSubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch extension subscription status
  const fetchStatus = useCallback(async (silent = false) => {
    if (!currentUser) {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      const token = await currentUser.getIdToken();

      const response = await axios.get('/api/extension/subscription/status', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.ok) {
        setStatus(response.data);
        setError(null);
      } else {
        setError('Failed to fetch extension status');
      }
    } catch (err) {
      console.error('[useExtensionSubscription] Error:', err);
      // Set default status on error
      setStatus({
        tier: 'free',
        trialActive: false,
        trialDaysRemaining: 0,
        trialStartDate: null,
        canSync: true,
        cloudSessionCount: 0,
        cloudSessionLimit: 10,
        pricing: {
          price: 1.99,
          isDiscounted: false,
          daysRemaining: 0,
          canonicalPrice: 1.99,
          discountedPrice: 0.99
        },
        features: {
          unlimitedSync: false,
          bulkExport: false,
          voiceRecording: false,
          voiceView: true,
          multiDevice: false
        },
        deviceLimit: 1
      });
      setError(err.response?.data?.error || 'Failed to load extension status');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser]);

  // Load on mount and user change
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Refresh every 5 minutes
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      fetchStatus(true);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser, fetchStatus]);

  // Helper functions
  const isPro = () => status?.tier === 'pro';
  const isTrial = () => status?.tier === 'trial';
  const isTrialExpired = () => status?.tier === 'trial_expired';
  const isFree = () => status?.tier === 'free';
  const canSync = () => status?.canSync ?? true;

  const hasDiscount = () => status?.pricing?.isDiscounted ?? false;
  const discountDaysRemaining = () => status?.pricing?.daysRemaining ?? 0;
  const currentPrice = () => status?.pricing?.price ?? 1.99;

  // Check if sync was blocked recently (within last 7 days)
  const hasSyncBlockedRecently = () => {
    if (!status?.lastSyncBlockedAt) return false;
    const blockedAt = new Date(status.lastSyncBlockedAt);
    const daysSince = (Date.now() - blockedAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince <= 7;
  };

  const getUpgradeUrl = () => {
    // Use the webapp billing page
    return '/settings/billing';
  };

  const value = {
    status,
    loading,
    error,
    refresh: fetchStatus,
    // Tier helpers
    isPro,
    isTrial,
    isTrialExpired,
    isFree,
    canSync,
    // Pricing helpers
    hasDiscount,
    discountDaysRemaining,
    currentPrice,
    getUpgradeUrl,
    // Blocked sync helper
    hasSyncBlockedRecently
  };

  return (
    <ExtensionSubscriptionContext.Provider value={value}>
      {children}
    </ExtensionSubscriptionContext.Provider>
  );
};

export default useExtensionSubscription;
